
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import Layout from '@kronoss/bricks/Layout/Layout';
import { Container } from '@mui/material';
import Head from 'next/head';
import useTranslation from 'next-translate/useTranslation';
import styles from '@kronoss/styles/pages/legal.module.scss';
const PrivacyPolicyPage = () => {
    const { t } = useTranslation('privacy-policy');
    const personalDataItems: string[] = t('sections.informationWeCollect.personalData.items', {}, { returnObjects: true });
    const childrenDataItems: string[] = t('sections.informationWeCollect.childrenData.items', {}, { returnObjects: true });
    const educationalDataItems: string[] = t('sections.informationWeCollect.educationalData.items', {}, { returnObjects: true });
    const financialDataItems: string[] = t('sections.informationWeCollect.financialData.items', {}, { returnObjects: true });
    const technicalDataItems: string[] = t('sections.informationWeCollect.technicalData.items', {}, { returnObjects: true });
    const howWeUseItems: string[] = t('sections.howWeUse.items', {}, { returnObjects: true });
    const thirdPartyServices: {
        name: string;
        purpose: string;
    }[] = t('sections.thirdPartyServices.services', {}, { returnObjects: true });
    const cookieCategories: {
        name: string;
        description: string;
    }[] = t('sections.cookies.categories', {}, { returnObjects: true });
    const dataRetentionItems: string[] = t('sections.dataRetention.items', {}, { returnObjects: true });
    const dataSecurityItems: string[] = t('sections.dataSecurity.items', {}, { returnObjects: true });
    const yourRightsItems: string[] = t('sections.yourRights.items', {}, { returnObjects: true });
    return (<>
      <Head>
        <title>{t('metainfo:privacy.title')}</title>
        <link rel="canonical" href="https://kronoss.education/privacy-policy"/>
        <link rel="alternate" hrefLang="en" href="https://kronoss.education/en/privacy-policy"/>
        <link rel="alternate" hrefLang="es" href="https://kronoss.education/es/privacy-policy"/>
        <meta name="description" content={t('metainfo:privacy.description')}/>
        <meta property="og:title" content={t('metainfo:privacy.slogan')}/>
        <meta property="og:description" content={t('metainfo:privacy.description')}/>
        <meta name="twitter:title" content={t('metainfo:privacy.slogan')}/>
        <meta name="twitter:description" content={t('metainfo:privacy.description')}/>
      </Head>
      <Layout>
        <Container>
          <div className={styles.LegalPage}>
            <h1 className={styles.Title}>{t('title')}</h1>
            <p className={styles.LastUpdated}>{t('lastUpdated')}</p>
            <p className={styles.Intro}>{t('intro')}</p>

            {/* 1. Information We Collect */}
            <div className={styles.Section}>
              <h2 className={styles.SectionTitle}>
                {t('sections.informationWeCollect.title')}
              </h2>

              {/* 1.1 Personal Data */}
              <div className={styles.SubSection}>
                <h3 className={styles.SubSectionTitle}>
                  {t('sections.informationWeCollect.personalData.title')}
                </h3>
                <p className={styles.Description}>
                  {t('sections.informationWeCollect.personalData.description')}
                </p>
                <ul className={styles.List}>
                  {personalDataItems.map((item, i) => (<li key={i}>{item}</li>))}
                </ul>
              </div>

              {/* 1.2 Children's Data */}
              <div className={styles.SubSection}>
                <h3 className={styles.SubSectionTitle}>
                  {t('sections.informationWeCollect.childrenData.title')}
                </h3>
                <p className={styles.Description}>
                  {t('sections.informationWeCollect.childrenData.description')}
                </p>
                <ul className={styles.List}>
                  {childrenDataItems.map((item, i) => (<li key={i}>{item}</li>))}
                </ul>
                <p className={styles.Notice}>
                  {t('sections.informationWeCollect.childrenData.notice')}
                </p>
              </div>

              {/* 1.3 Educational Data */}
              <div className={styles.SubSection}>
                <h3 className={styles.SubSectionTitle}>
                  {t('sections.informationWeCollect.educationalData.title')}
                </h3>
                <p className={styles.Description}>
                  {t('sections.informationWeCollect.educationalData.description')}
                </p>
                <ul className={styles.List}>
                  {educationalDataItems.map((item, i) => (<li key={i}>{item}</li>))}
                </ul>
              </div>

              {/* 1.4 Financial Data */}
              <div className={styles.SubSection}>
                <h3 className={styles.SubSectionTitle}>
                  {t('sections.informationWeCollect.financialData.title')}
                </h3>
                <p className={styles.Description}>
                  {t('sections.informationWeCollect.financialData.description')}
                </p>
                <ul className={styles.List}>
                  {financialDataItems.map((item, i) => (<li key={i}>{item}</li>))}
                </ul>
                <p className={styles.Notice}>
                  {t('sections.informationWeCollect.financialData.notice')}
                </p>
              </div>

              {/* 1.5 Technical Data */}
              <div className={styles.SubSection}>
                <h3 className={styles.SubSectionTitle}>
                  {t('sections.informationWeCollect.technicalData.title')}
                </h3>
                <p className={styles.Description}>
                  {t('sections.informationWeCollect.technicalData.description')}
                </p>
                <ul className={styles.List}>
                  {technicalDataItems.map((item, i) => (<li key={i}>{item}</li>))}
                </ul>
              </div>
            </div>

            {/* 2. How We Use Your Information */}
            <div className={styles.Section}>
              <h2 className={styles.SectionTitle}>
                {t('sections.howWeUse.title')}
              </h2>
              <p className={styles.Description}>
                {t('sections.howWeUse.description')}
              </p>
              <ul className={styles.List}>
                {howWeUseItems.map((item, i) => (<li key={i}>{item}</li>))}
              </ul>
            </div>

            {/* 3. Third-Party Services */}
            <div className={styles.Section}>
              <h2 className={styles.SectionTitle}>
                {t('sections.thirdPartyServices.title')}
              </h2>
              <p className={styles.Description}>
                {t('sections.thirdPartyServices.description')}
              </p>
              {thirdPartyServices.map((service, i) => (<div key={i} className={styles.ServiceItem}>
                  <span className={styles.ServiceName}>
                    {service.name}
                  </span>
                  {' — '}
                  <span className={styles.ServicePurpose}>
                    {service.purpose}
                  </span>
                </div>))}
              <p className={styles.Notice}>
                {t('sections.thirdPartyServices.notice')}
              </p>
            </div>

            {/* 4. Cookies */}
            <div className={styles.Section}>
              <h2 className={styles.SectionTitle}>
                {t('sections.cookies.title')}
              </h2>
              <p className={styles.Description}>
                {t('sections.cookies.description')}
              </p>
              <ul className={styles.List}>
                {cookieCategories.map((cat, i) => (<li key={i}>
                    <strong>{cat.name}</strong>
                    {' — '}
                    {cat.description}
                  </li>))}
              </ul>
              <p className={styles.Notice}>
                {t('sections.cookies.notice')}
              </p>
            </div>

            {/* 5. Data Retention */}
            <div className={styles.Section}>
              <h2 className={styles.SectionTitle}>
                {t('sections.dataRetention.title')}
              </h2>
              <p className={styles.Description}>
                {t('sections.dataRetention.description')}
              </p>
              <ul className={styles.List}>
                {dataRetentionItems.map((item, i) => (<li key={i}>{item}</li>))}
              </ul>
              <p className={styles.Notice}>
                {t('sections.dataRetention.notice')}
              </p>
            </div>

            {/* 6. Data Security */}
            <div className={styles.Section}>
              <h2 className={styles.SectionTitle}>
                {t('sections.dataSecurity.title')}
              </h2>
              <p className={styles.Description}>
                {t('sections.dataSecurity.description')}
              </p>
              <ul className={styles.List}>
                {dataSecurityItems.map((item, i) => (<li key={i}>{item}</li>))}
              </ul>
            </div>

            {/* 7. Your Rights */}
            <div className={styles.Section}>
              <h2 className={styles.SectionTitle}>
                {t('sections.yourRights.title')}
              </h2>
              <p className={styles.Description}>
                {t('sections.yourRights.description')}
              </p>
              <ul className={styles.List}>
                {yourRightsItems.map((item, i) => (<li key={i}>{item}</li>))}
              </ul>
              <p className={styles.Notice}>
                {t('sections.yourRights.notice')}
              </p>
            </div>

            {/* 8. International Transfers */}
            <div className={styles.Section}>
              <h2 className={styles.SectionTitle}>
                {t('sections.internationalTransfers.title')}
              </h2>
              <p className={styles.Description}>
                {t('sections.internationalTransfers.description')}
              </p>
            </div>

            {/* 9. Changes */}
            <div className={styles.Section}>
              <h2 className={styles.SectionTitle}>
                {t('sections.changes.title')}
              </h2>
              <p className={styles.Description}>
                {t('sections.changes.description')}
              </p>
            </div>

            {/* 10. Contact */}
            <div className={styles.Section}>
              <h2 className={styles.SectionTitle}>
                {t('sections.contact.title')}
              </h2>
              <p className={styles.Description}>
                {t('sections.contact.description')}
              </p>
              <a href={`mailto:${t('sections.contact.email')}`} className={styles.EmailLink}>
                {t('sections.contact.email')}
              </a>
            </div>
          </div>
        </Container>
      </Layout>
    </>);
};
export default PrivacyPolicyPage;

    async function __Next_Translate__getStaticProps__19c38b8bf85__(ctx) {
      
      return {
        
        
        props: {
          
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/privacy-policy',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || (() => Promise.resolve({})),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__19c38b8bf85__ as getStaticProps }
  